import { useState, useEffect, memo } from 'react';
import { LeadGenWidget } from './LeadGenWidget.tsx';
import { checkShowConditions } from './emailSignupConditions.ts';
import { MinimizedLeadGenWidget } from './MinimizedLeadGenWidget.tsx';

interface MaybeLeadGenWidgetProps {
    // Current value of the leadgen feature-flag
    leadGenEnabled: boolean,
    // Current value of the "minimized" leadgen feature-flag (replacing leadgen with a tiny, ignorable button that will
    // expand into an email-signup-form on click)
    minimizedLeadGenEnabled: boolean,
    // Scroll-handlers activate a "lead gen" element when the element with this ID enters the viewport. Defaults to the
    // global footer's ID if blank. (Note that _which_ element is activated depends on the "minimized" flag etc.)
    targetElementId?: string,
}

/**
 * A wrapper around {@link LeadGenWidget} that renders nothing by default but will, on mount, check our "should we show
 * {@link LeadGenWidget}?" rules and conditionally render a {@link LeadGenWidget} based on those results.
 *
 * We originally did these checks outside React, but that wouldn't play nicely with an all-React page like the homepage.
 *
 * @param leadGenEnabled Current value of the leadgen feature-flag.
 */
export let MaybeLeadGenWidget = ({ leadGenEnabled, minimizedLeadGenEnabled, targetElementId }: MaybeLeadGenWidgetProps) => {
    const [show, setShow] = useState(false);
    const [showMinimized, setShowMinimized] = useState(false);

    useEffect(() => {
        let onSuccess;
        if (minimizedLeadGenEnabled) {
            onSuccess = () => { setShowMinimized(true); };
        } else {
            onSuccess = () => { setShow(true); };
        }

        // "Minimized" lead gen attaches to different elements on different pages, but "classic" lead gen is always
        // attached to the footer.
        const maybeTargetElementId = minimizedLeadGenEnabled ? targetElementId : undefined;
        checkShowConditions(leadGenEnabled, minimizedLeadGenEnabled, onSuccess, maybeTargetElementId);

        // We want this to run only once on mount, so leave the deps array empty:
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (showMinimized) {
        const removeLeadGenWidget = () => { setShowMinimized(false); };

        return <MinimizedLeadGenWidget removeLeadGenWidget={removeLeadGenWidget} />;
    } else if (show) {
        return <LeadGenWidget minimizedLeadGenEnabled={minimizedLeadGenEnabled} />;
    } else {
        return null;
    }
};
MaybeLeadGenWidget = memo(MaybeLeadGenWidget);
